import type { Mutation, Operation, StockageLocal } from '../types.js';

/**
 * Journal de mutations de l'appareil (L22). Toute écriture est d'abord gardée localement avec une clé d'idempotence,
 * dans l'ordre de saisie, avant d'être envoyée. Il survit au redémarrage (stockage local : Dexie en production).
 * Disposition des clés : `journal:<rang sur 10 chiffres>` (file d'envoi), `meta:ordre` (dernier rang attribué),
 * `conflits:<mutation_id>` (mutations refusées par le serveur, à présenter à l'utilisateur).
 */
export const PREFIXE_JOURNAL = 'journal:';
export const PREFIXE_CONFLITS = 'conflits:';
const CLE_ORDRE = 'meta:ordre';

export interface OptionsJournal {
  readonly stockage: StockageLocal;
  /** Instant ISO 8601 avec décalage explicite. */
  readonly horloge: () => string;
  /** Générateur d'UUID v7. */
  readonly genererId: () => string;
}

export interface ConflitLocal {
  readonly mutation: Mutation;
  /** Réponse du serveur : champs en conflit, ou code de rejet. */
  readonly detail: Record<string, unknown>;
}

export interface Journal {
  enregistrer(entityType: string, entityId: string, baseVersion: number, op: Operation): Promise<Mutation>;
  enAttente(): Promise<Mutation[]>;
  compter(): Promise<number>;
  retirer(mutationIds: readonly string[]): Promise<void>;
  signalerConflit(mutationId: string, detail: Record<string, unknown>): Promise<void>;
  conflits(): Promise<ConflitLocal[]>;
  /** « garder_local » remet en file une NOUVELLE mutation calée sur la version du serveur ; « garder_serveur » abandonne la saisie. */
  resoudre(mutationId: string, choix: 'garder_local' | 'garder_serveur'): Promise<Mutation | null>;
}

const cleJournal = (rang: number): string => `${PREFIXE_JOURNAL}${String(rang).padStart(10, '0')}`;

export function creerJournal({ stockage, horloge, genererId }: OptionsJournal): Journal {
  const entrees = async (): Promise<{ cle: string; mutation: Mutation }[]> => {
    const cles = await stockage.cles(PREFIXE_JOURNAL);
    const lues = await Promise.all(cles.map(async (cle) => ({ cle, mutation: (await stockage.lire<Mutation>(cle)) as Mutation })));
    return lues;
  };
  const trouver = async (mutationId: string) => (await entrees()).find((e) => e.mutation.mutationId === mutationId);

  const journal: Journal = {
    async enregistrer(entityType, entityId, baseVersion, op) {
      const rang = ((await stockage.lire<number>(CLE_ORDRE)) ?? 0) + 1;
      const mutation: Mutation = { mutationId: genererId(), entityType, entityId, baseVersion, op, creeeA: horloge() };
      await stockage.ecrire(cleJournal(rang), mutation);
      await stockage.ecrire(CLE_ORDRE, rang);
      return mutation;
    },
    async enAttente() {
      return (await entrees()).map((e) => e.mutation);
    },
    async compter() {
      return (await stockage.cles(PREFIXE_JOURNAL)).length;
    },
    async retirer(mutationIds) {
      for (const e of await entrees()) if (mutationIds.includes(e.mutation.mutationId)) await stockage.supprimer(e.cle);
    },
    async signalerConflit(mutationId, detail) {
      const e = await trouver(mutationId);
      if (!e) return;
      await stockage.ecrire(`${PREFIXE_CONFLITS}${mutationId}`, { mutation: e.mutation, detail });
      await stockage.supprimer(e.cle);
    },
    async conflits() {
      const cles = await stockage.cles(PREFIXE_CONFLITS);
      return Promise.all(cles.map(async (cle) => (await stockage.lire<ConflitLocal>(cle)) as ConflitLocal));
    },
    async resoudre(mutationId, choix) {
      const cle = `${PREFIXE_CONFLITS}${mutationId}`;
      const conflit = await stockage.lire<ConflitLocal>(cle);
      if (!conflit) return null;
      const { mutation, detail } = conflit;
      // Un rejet porte un code : le serveur est prioritaire, la même saisie ne se rejoue pas sur sa version.
      if (choix === 'garder_local' && typeof detail['code'] === 'string') {
        throw new Error(`Le serveur a refusé cette modification (${detail['code']}) : seule la version du serveur peut être gardée.`);
      }
      await stockage.supprimer(cle);
      if (choix === 'garder_serveur') return null;
      const versionServeur = typeof detail['versionServeur'] === 'number' ? detail['versionServeur'] : mutation.baseVersion;
      return journal.enregistrer(mutation.entityType, mutation.entityId, versionServeur, mutation.op);
    },
  };
  return journal;
}
